// src/components/HeadlineFlow.tsx
import React, { useMemo, useEffect, memo } from 'react';
import { headlineFlowData } from '../data/headlineData';
import HeadlineCard from './HeadlineCard';
import TransitionCard from './TransitionCard';
import { easeCubicInOut } from 'd3-ease';
import ReturnToTop from './ReturnToTop';

// Animation timing constants can remain outside
const FADE_IN_DELAY = 0.15;
const FADE_IN_DURATION = 0.20;
const PAUSE_AFTER_FADE = 0.05;
const SCROLL_START_PROGRESS = FADE_IN_DELAY + FADE_IN_DURATION + PAUSE_AFTER_FADE;

// Static effect constants can also remain outside
const CARD_ASPECT_RATIO = 2.5 / 1;
const FOCAL_POINT_Y_FRAC = 0.5;
const MAX_SCALE = 1.0;
const MIN_SCALE = 0.75;
const MAX_OPACITY = 1.0;
const MIN_OPACITY = 0.25;
const EFFECT_RANGE_FRAC = 0.75;

interface HeadlineFlowProps {
  scrollY: number;
  activeSection: number;
  thisFlowSectionIndex: number;
  viewportHeight: number;
  sectionStartVhValues: number[];
  sectionHeightVh: number;
  onProgressChange?: (progress: number) => void;
}

const HeadlineFlow: React.FC<HeadlineFlowProps> = ({
  scrollY,
  activeSection,
  thisFlowSectionIndex,
  viewportHeight,
  sectionStartVhValues,
  sectionHeightVh,
  onProgressChange,
}) => {
  // <<< START: DYNAMIC LAYOUT CONSTANTS >>>
  const layout = useMemo(() => {
    const isMobile = window.innerWidth < 768;

    const cardWidth = isMobile ? window.innerWidth * 0.9 : 750;
    const cardHeight = cardWidth / CARD_ASPECT_RATIO;
    // Scale spacing proportionally to the card width
    const cardSpacing = isMobile ? cardWidth * 0.6 : 280;

    return {
      FOCUSED_CARD_WIDTH_PX: cardWidth,
      ESTIMATED_FOCUSED_CARD_HEIGHT_PX: cardHeight,
      CARD_SPACING_PX: cardSpacing,
    };
  }, []);
  // <<< END: DYNAMIC LAYOUT CONSTANTS >>>

  const isActive = activeSection === thisFlowSectionIndex;

  // Progress calculation logic (no changes here)
  const sectionProgress = useMemo(() => {
      if (viewportHeight <= 0) return 0;
      const sectionStartVh = sectionStartVhValues[thisFlowSectionIndex] || 0;
      const sectionStartPx = ((sectionStartVh + 1) / 100) * viewportHeight;
      const sectionHeightPx = (sectionHeightVh / 100) * viewportHeight;
      const totalDocumentHeightPx = sectionStartPx + sectionHeightPx;
      const maxScrollY = totalDocumentHeightPx - viewportHeight;
      const effectiveScrollableDistancePx = maxScrollY - sectionStartPx;
      if (effectiveScrollableDistancePx <= 0) return 1;
      const scrollInSection = scrollY - sectionStartPx;
      const progress = scrollInSection / effectiveScrollableDistancePx;
      return Math.max(0, Math.min(1, progress));
    }, [scrollY, viewportHeight, thisFlowSectionIndex, sectionStartVhValues, sectionHeightVh]);

  useEffect(() => {
    onProgressChange?.(sectionProgress);
  }, [sectionProgress, onProgressChange]);


  const flowContainerOpacity = isActive
    ? Math.max(0, Math.min(1, (sectionProgress - FADE_IN_DELAY) / FADE_IN_DURATION))
    : 0;

  const adjustedScrollProgress = Math.max(0, (sectionProgress - SCROLL_START_PROGRESS) / (1 - SCROLL_START_PROGRESS));

  // Position ReturnToTop button below the last card
const lastCardTopPosition = (headlineFlowData.length - 1) * layout.CARD_SPACING_PX;
const buttonTopPosition = lastCardTopPosition + layout.ESTIMATED_FOCUSED_CARD_HEIGHT_PX + 40;
const buttonOpacity = adjustedScrollProgress > 0.8 ? Math.min(1, (adjustedScrollProgress - 0.8) / 0.2) : 0;

const returnToTopStyle: React.CSSProperties = {
  position: 'absolute',
  top: `${buttonTopPosition}px`,
  left: '0',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  opacity: buttonOpacity,
  transition: 'opacity 0.3s ease-in-out',
  pointerEvents: buttonOpacity > 0.1 ? 'auto' : 'none',
};
  
  // Use dynamic layout constants
  const totalListScrollTravel = (headlineFlowData.length - 1) * layout.CARD_SPACING_PX; // <<< CHANGED
  const listScrollOffsetY = adjustedScrollProgress * totalListScrollTravel;
  const focalPointY = viewportHeight * FOCAL_POINT_Y_FRAC;
  const initialPlaneOffset = focalPointY - (layout.ESTIMATED_FOCUSED_CARD_HEIGHT_PX / 2); // <<< CHANGED

  const headlinesContainerStyle: React.CSSProperties = {
    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
    zIndex: isActive ? 20 : 5, opacity: flowContainerOpacity,
    visibility: flowContainerOpacity > 0 ? 'visible' : 'hidden',
    pointerEvents: 'none', overflow: 'hidden',
  };

  const cardsColumnStyle: React.CSSProperties = {
    position: 'absolute',
    width: `${layout.FOCUSED_CARD_WIDTH_PX}px`, // <<< CHANGED
    top: `${initialPlaneOffset - listScrollOffsetY}px`,
    left: '50%', transform: 'translateX(-50%)',
    pointerEvents: isActive && flowContainerOpacity > 0.5 ? 'auto' : 'none',
  };

  if (flowContainerOpacity < 0.01 && !isActive) {
    return null;
  }

  return (
    <>
      <TransitionCard
        text="Then sharing stories"
        exitStyle="scrollOff"
        isVisible={isActive && sectionProgress < SCROLL_START_PROGRESS}
        progress={sectionProgress / SCROLL_START_PROGRESS}
        endYPosition={initialPlaneOffset - 50}
      />
      <div style={headlinesContainerStyle}>
        <div style={cardsColumnStyle}>
          {headlineFlowData.map((item, index) => {
            const cardNaturalTopInColumn = index * layout.CARD_SPACING_PX; // <<< CHANGED
            const planeTopInViewport = initialPlaneOffset - listScrollOffsetY;
            const currentCardEstimatedHeight = layout.ESTIMATED_FOCUSED_CARD_HEIGHT_PX * MAX_SCALE; // <<< CHANGED
            const cardCenterInViewport = planeTopInViewport + cardNaturalTopInColumn + (currentCardEstimatedHeight / 2);
            const distanceToFocal = cardCenterInViewport - focalPointY;
            const rawNormalizedDistance = Math.min(1, Math.abs(distanceToFocal) / (viewportHeight * EFFECT_RANGE_FRAC));
            const easedNormalizedDistance = easeCubicInOut(rawNormalizedDistance);
            let scale = MAX_SCALE - easedNormalizedDistance * (MAX_SCALE - MIN_SCALE);
            let opacity = MAX_OPACITY - easedNormalizedDistance * (MAX_OPACITY - MIN_OPACITY);
            scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
            opacity = Math.max(MIN_OPACITY, Math.min(MAX_OPACITY, opacity));
            const zIndexCard = Math.round(MAX_OPACITY * 100) +
              (Math.abs(distanceToFocal) < layout.CARD_SPACING_PX / 4 ? headlineFlowData.length + 1 : Math.round(MAX_OPACITY * 100 - easedNormalizedDistance * 10)); // <<< CHANGED

            return (
              <HeadlineCard
                key={item.id} item={item} scale={scale} opacity={opacity}
                topInColumn={cardNaturalTopInColumn}
                cardWidth={layout.FOCUSED_CARD_WIDTH_PX} // <<< CHANGED
                zIndex={zIndexCard}
              />
            );
          })}
          
          <div style={returnToTopStyle}>
  <ReturnToTop 
    isVisible={true}
    onClick={() => {
      window.scrollTo(0, 0);
      setTimeout(() => window.location.reload(), 50);
    }}
  />
</div>
        </div>
      </div>
    </>
  );
};

export default memo (HeadlineFlow);